import type { Email, FTLShipment, GetAllEmailsResponse, LTLShipment } from "@src/types/api";
import { LoadAgentHttpTransport } from "./http/LoadAgentHttpTransport";
import type {
  CreateLTLShipmentRequest,
  CreateShipmentRequest,
  CreateShipmentResponse,
  DocumentInfo,
  GetDocumentsRequest,
  PricingFetchResult,
  PricingRequestIdentity,
  RecordEmailInteractionLeadRequest,
  ResetRequiredFieldsRequest,
  SaveEmailFeedbackRequest,
  SaveEmailFeedbackResponse,
  SaveLTLPricingRequest,
  SavePricingRequest,
  SaveSelectedOfferRequest,
  UpdateLoadStatusRequest,
  BookingResponse,
} from "./types/contracts";

type CreateJson = {
  shipmentId?: string;
  shipmentID?: string;
  shipment?: unknown;
};

function getDocumentsApiPrefix(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return process.env.NODE_ENV === "development"
      ? `${window.location.origin}/api/`
      : `${window.location.origin}/`;
  }
  return process.env.NODE_ENV === "development" ? "/api/" : "";
}

async function listTmsDocuments(
  loadId: number,
  internetMessageId: string,
  mode: "LTL" | "FTL"
): Promise<DocumentInfo[]> {
  const prefix = getDocumentsApiPrefix();
  const q = new URLSearchParams({ internetMessageId, loadId: String(loadId), mode });
  const res = await fetch(`${prefix}documents/tms-list?${q.toString()}`, { method: "GET" });

  if (!res.ok) {
    const text = await res.text().catch(() => "");

    throw new Error(
      text
        ? `Failed to fetch documents: ${res.status} - ${text}`
        : `Failed to fetch documents: ${res.status}`
    );
  }
  return (await res.json()) as DocumentInfo[];
}

export class LoadApplication {
  private readonly http = new LoadAgentHttpTransport();
  async getAllEmails(
    internetMessageId: string | undefined,
    signal?: AbortSignal,
    conversationId?: string | null,
    threadCandidateIds?: string[]
  ): Promise<GetAllEmailsResponse> {
    const params = new URLSearchParams();
    const im = typeof internetMessageId === "string" ? internetMessageId.trim() : "";

    // Send every identifier we can so the server can resolve the org anchor even when the open
    // item's internetMessageId is absent (drafts) or not yet persisted.
    if (im) params.set("internetMessageId", im);
    if (conversationId?.trim()) params.set("conversationId", conversationId.trim());
    for (const c of threadCandidateIds ?? []) {
      const t = typeof c === "string" ? c.trim() : "";

      if (t) params.append("candidate", t);
    }
    const qs = params.toString();

    if (!qs) {
      throw new Error(
        "getAllEmails requires internetMessageId, conversationId, or at least one candidate message id"
      );
    }
    return this.http.getJson<GetAllEmailsResponse>(`emails/getAll?${qs}`, {
      method: "GET",
      signal,
    });
  }
  async getEmails(
    internetMessageId: string | undefined,
    signal?: AbortSignal,
    conversationId?: string | null,
    threadCandidateIds?: string[],
    ewsItemId?: string | null
  ): Promise<Email | null> {
    const params = new URLSearchParams();
    const im = typeof internetMessageId === "string" ? internetMessageId.trim() : "";

    if (im) params.set("internetMessageId", im);
    if (conversationId?.trim()) params.set("conversationId", conversationId.trim());
    for (const c of threadCandidateIds ?? []) {
      const t = typeof c === "string" ? c.trim() : "";

      if (t) params.append("candidate", t);
    }
    if (ewsItemId?.trim()) params.set("ewsItemId", ewsItemId.trim());
    const qs = params.toString();

    if (!qs) {
      throw new Error(
        "getEmails requires internetMessageId, conversationId, or at least one candidate message id"
      );
    }
    return this.http.getJson<Email | null>(`emails/getEmails?${qs}`, {
      method: "GET",
      signal,
    });
  }
  saveEmailFeedback(req: SaveEmailFeedbackRequest): Promise<SaveEmailFeedbackResponse> {
    return this.http.postJson<SaveEmailFeedbackResponse>("emails/feedback", req);
  }
  async recordEmailInteractionLead(req: RecordEmailInteractionLeadRequest): Promise<void> {
    await this.http.postNoContent("emails/recordEmailInteractionLead", req);
  }
  async createLTLShipment<TFormData extends Record<string, unknown>>(
    req: CreateLTLShipmentRequest<TFormData>
  ): Promise<CreateShipmentResponse<LTLShipment>> {
    const data = await this.http.postJson<CreateJson>("shipments", { ...req, mode: "LTL" });

    return {
      shipmentId: data.shipmentId ?? data.shipmentID ?? "",
      shipment: data.shipment as LTLShipment,
    };
  }
  async saveLTLPricing<TFormData extends Record<string, unknown>>(
    req: SaveLTLPricingRequest<TFormData>
  ): Promise<void> {
    await this.http.postNoContent(`shipments/${encodeURIComponent(req.shipmentId)}/pricing`, req);
  }
  async resetLTLRequiredFields(req: ResetRequiredFieldsRequest): Promise<void> {
    await this.http.postNoContent(
      `shipments/${encodeURIComponent(req.shipmentId)}/reset-required-fields`,
      req
    );
  }
  getLTLPricing(
    shipmentId: string,
    internetMessageId: string,
    identity?: PricingRequestIdentity
  ): Promise<PricingFetchResult> {
    return this.http.postLoadAgentQuote(shipmentId, { internetMessageId, ...(identity ?? {}) });
  }
  async saveLTLSelectedOffer(req: SaveSelectedOfferRequest): Promise<void> {
    await this.http.postNoContent(
      `shipments/${encodeURIComponent(req.shipmentId)}/selected-offer`,
      req
    );
  }
  async updateLTLLoadStatus(req: UpdateLoadStatusRequest): Promise<void> {
    await this.http.patchNoContent(
      `shipments/${encodeURIComponent(req.shipmentId)}/load-status`,
      req
    );
  }
  async createFTLShipment<TFormData extends Record<string, unknown>>(
    req: CreateShipmentRequest<TFormData>
  ): Promise<CreateShipmentResponse<FTLShipment>> {
    const data = await this.http.postJson<CreateJson>("shipments", { ...req, mode: "FTL" });

    return {
      shipmentId: data.shipmentId ?? data.shipmentID ?? "",
      shipment: data.shipment as FTLShipment,
    };
  }
  async saveFTLPricing<TFormData extends Record<string, unknown>>(
    req: SavePricingRequest<TFormData>
  ): Promise<void> {
    await this.http.postNoContent(`shipments/${encodeURIComponent(req.shipmentId)}/pricing`, req);
  }
  async resetFTLRequiredFields(req: ResetRequiredFieldsRequest): Promise<void> {
    await this.http.postNoContent(
      `shipments/${encodeURIComponent(req.shipmentId)}/reset-required-fields`,
      req
    );
  }
  getFTLPricing(
    shipmentId: string,
    internetMessageId: string,
    identity?: PricingRequestIdentity
  ): Promise<PricingFetchResult> {
    return this.http.postLoadAgentQuote(shipmentId, { internetMessageId, ...(identity ?? {}) });
  }
  async saveFTLSelectedOffer(req: SaveSelectedOfferRequest): Promise<void> {
    await this.http.postNoContent(
      `shipments/${encodeURIComponent(req.shipmentId)}/selected-offer`,
      req
    );
  }
  async updateFTLLoadStatus(req: UpdateLoadStatusRequest): Promise<void> {
    await this.http.patchNoContent(
      `shipments/${encodeURIComponent(req.shipmentId)}/load-status`,
      req
    );
  }
  getDocuments(req: GetDocumentsRequest): Promise<DocumentInfo[]> {
    return listTmsDocuments(req.loadId, req.internetMessageId.trim(), req.mode);
  }
  async postLoadAgentReplyDraftHtml(params: {
    internetMessageId: string;
    shipmentIds: string[];
    prospectDisplayName?: string;
  }): Promise<string> {
    const r = await this.http.postLoadAgentReplyDraftHtml(params);

    return typeof r.html === "string" ? r.html : "";
  }
  async postLoadAgentBook(params: {
    shipmentId: string;
    internetMessageId: string;
    selectedOfferId?: string;
  }): Promise<BookingResponse> {
    const r = await this.http.postLoadAgentBook(params.shipmentId, {
      internetMessageId: params.internetMessageId,
      selectedOfferId: params.selectedOfferId,
    });
    const loadId =
      typeof r.loadId === "number" && Number.isFinite(r.loadId)
        ? Math.trunc(r.loadId)
        : r.confirmationId && /^\d+$/.test(r.confirmationId.trim())
          ? Number(r.confirmationId.trim())
          : Math.floor(Math.random() * 900000) + 100000;
    const replyAttachments = Array.isArray(r.replyAttachments) ? r.replyAttachments : [];
    const confirmationHtml =
      typeof r.confirmationHtml === "string" && r.confirmationHtml.length > 0
        ? r.confirmationHtml
        : "";

    if (!confirmationHtml || replyAttachments.length === 0) {
      throw new Error("Book response missing confirmationHtml or replyAttachments");
    }
    return {
      loadId,
      bookAttachments: r.bookAttachments,
      confirmationHtml,
      replyAttachments,
    };
  }
}
export type LoadApplicationWiring = Pick<
  LoadApplication,
  | "getAllEmails"
  | "getEmails"
  | "saveEmailFeedback"
  | "recordEmailInteractionLead"
  | "createLTLShipment"
  | "saveLTLPricing"
  | "resetLTLRequiredFields"
  | "getLTLPricing"
  | "saveLTLSelectedOffer"
  | "updateLTLLoadStatus"
  | "createFTLShipment"
  | "saveFTLPricing"
  | "resetFTLRequiredFields"
  | "getFTLPricing"
  | "saveFTLSelectedOffer"
  | "updateFTLLoadStatus"
  | "getDocuments"
  | "postLoadAgentReplyDraftHtml"
  | "postLoadAgentBook"
>;
